import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { BarChart3, Mic, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchKaraokeLiveSessionMetrics } from '@/lib/karaokeSessionAnalytics';
import {
  visitsForSessionFunnel,
  filterInteractionsForSession,
} from '@/lib/sessionFunnelQueries';
import { isPaidSongRequestForLedger } from '@/lib/payoutLedger';

function toStartDate(sessionStartTime) {
  if (!sessionStartTime) return null;
  if (typeof sessionStartTime.toDate === 'function') return sessionStartTime.toDate();
  if (sessionStartTime.seconds != null) return new Date(sessionStartTime.seconds * 1000);
  const d = new Date(sessionStartTime);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Funnel widgets for admin live sessions (same bounds logic as session history on end). */
export const LiveSessionAnalytics = ({ artistId, sessionId, sessionStartTime }) => {
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    songsAddedToCart: 0,
    checkouts: 0,
    tipCount: 0,
    totalTips: 0,
    conversionRate: 0,
    loading: true,
  });
  const visitsRef = useRef([]);
  const interactionsRef = useRef([]);
  const requestsRef = useRef([]);

  const recompute = useCallback(() => {
    const start = toStartDate(sessionStartTime);
    if (!artistId || !sessionId || !start) {
      setAnalytics((prev) => ({ ...prev, loading: false }));
      return;
    }

    const now = new Date();
    const sessionVisits = visitsForSessionFunnel(visitsRef.current, sessionId, start, now);
    const uniqueVisitors = new Set(sessionVisits.map((v) => v.visitor_id).filter(Boolean)).size;
    const totalViews = sessionVisits.length;

    const interactions = filterInteractionsForSession(interactionsRef.current, sessionId, start, now);
    const songsAddedToCart = interactions.filter((i) => i.interaction_type === 'added_to_cart').length;

    const sessionRequests = requestsRef.current;
    const checkouts = sessionRequests.filter((r) => isPaidSongRequestForLedger(r)).length;
    const requestTipTotal = sessionRequests.reduce((sum, r) => sum + (Number(r.tip) || 0), 0);
    const requestTipCount = sessionRequests.filter((r) => (Number(r.tip) || 0) > 0).length;

    const conversionRate =
      songsAddedToCart > 0
        ? ((checkouts / songsAddedToCart) * 100).toFixed(2)
        : checkouts > 0
          ? '100.00'
          : '0';

    setAnalytics({
      totalViews,
      uniqueVisitors,
      songsAddedToCart,
      checkouts,
      tipCount: requestTipCount,
      totalTips: requestTipTotal,
      conversionRate: parseFloat(conversionRate),
      loading: false,
    });
  }, [artistId, sessionId, sessionStartTime]);

  useEffect(() => {
    if (!artistId || !sessionId || !toStartDate(sessionStartTime)) {
      setAnalytics((prev) => ({ ...prev, loading: false }));
      return undefined;
    }

    visitsRef.current = [];
    interactionsRef.current = [];
    requestsRef.current = [];

    const unsubVisits = onSnapshot(
      query(collection(db, 'page_visits'), where('artist_id', '==', artistId)),
      (snap) => {
        visitsRef.current = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recompute();
      },
      (error) => {
        console.warn('Live analytics page_visits listener:', error?.message || error);
        recompute();
      }
    );

    const unsubInteractions = onSnapshot(
      query(collection(db, 'song_interactions'), where('artist_id', '==', artistId)),
      (snap) => {
        interactionsRef.current = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recompute();
      },
      (error) => {
        console.warn('Live analytics song_interactions listener:', error?.message || error);
        recompute();
      }
    );

    const unsubRequests = onSnapshot(
      query(
        collection(db, 'requests'),
        where('artist_id', '==', artistId),
        where('session_id', '==', sessionId)
      ),
      (snap) => {
        requestsRef.current = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        recompute();
      },
      (error) => {
        console.warn('Live analytics requests listener:', error?.message || error);
        recompute();
      }
    );

    return () => {
      unsubVisits();
      unsubInteractions();
      unsubRequests();
    };
  }, [artistId, sessionId, sessionStartTime, recompute]);

  if (analytics.loading) {
    return (
      <div className="bg-gray-700 rounded-lg p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-center text-gray-400 mt-2 text-sm">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 border border-purple-500/40 rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-purple-400" />
          <label className="text-sm font-semibold text-white">Live Session Analytics</label>
          <span className="text-xs text-green-400 flex items-center">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></span>
            Live
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
        <div>
          <p className="text-xs text-gray-400 mb-1">Total Views</p>
          <p className="text-xl font-bold text-white">{analytics.totalViews}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Unique Visitors</p>
          <p className="text-xl font-bold text-blue-400">{analytics.uniqueVisitors}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Added to Cart</p>
          <p className="text-xl font-bold text-yellow-400">{analytics.songsAddedToCart}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Checkouts</p>
          <p className="text-xl font-bold text-green-400">{analytics.checkouts}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Tips</p>
          <p className="text-xl font-bold text-rose-400">{analytics.tipCount}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Tip total</p>
          <p className="text-xl font-bold text-rose-300">{analytics.totalTips.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Conversion Rate</p>
          <p className="text-xl font-bold text-purple-400">{analytics.conversionRate}%</p>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-3 text-center">
        Updates live as fans view the page, add songs, and check out
      </p>
    </div>
  );
};

export const KaraokeSessionAnalytics = ({ artistId, sessionId, sessionStartTime }) => {
  const [analytics, setAnalytics] = useState({
    totalViews: 0,
    uniqueVisitors: 0,
    freeSongRequests: 0,
    paidSongsAddedToBasket: 0,
    paidSongsCheckout: 0,
    conversionPct: 0,
    loading: true,
  });
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    async (isInitial) => {
      if (!artistId || !sessionId || !sessionStartTime) {
        setAnalytics((prev) => ({ ...prev, loading: false }));
        return;
      }

      try {
        if (isInitial) setAnalytics((prev) => ({ ...prev, loading: true }));
        else setRefreshing(true);

        const m = await fetchKaraokeLiveSessionMetrics(artistId, sessionId, sessionStartTime, new Date());
        setAnalytics({
          totalViews: m.totalViews,
          uniqueVisitors: m.uniqueVisitors,
          freeSongRequests: m.freeSongRequests,
          paidSongsAddedToBasket: m.paidSongsAddedToBasket,
          paidSongsCheckout: m.paidSongsCheckout,
          conversionPct: m.conversionPct,
          loading: false,
        });
      } catch (e) {
        console.error('Error loading karaoke session analytics:', e);
        setAnalytics((prev) => ({ ...prev, loading: false }));
      } finally {
        setRefreshing(false);
      }
    },
    [artistId, sessionId, sessionStartTime]
  );

  useEffect(() => {
    load(true);
    const interval = setInterval(() => load(false), 15000);
    return () => clearInterval(interval);
  }, [load]);

  if (analytics.loading) {
    return (
      <div className="bg-gray-700 rounded-lg p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
        <p className="text-center text-gray-400 mt-2 text-sm">Loading karaoke analytics...</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-cyan-600/20 to-cyan-900/20 border border-cyan-500/40 rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center space-x-2">
          <Mic className="w-5 h-5 text-cyan-400" />
          <label className="text-sm font-semibold text-white">Karaoke session analytics</label>
          <span className="text-xs text-green-400 flex items-center">
            <span className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></span>
            Live
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="border-cyan-500/50 text-cyan-200 hover:bg-cyan-900/40"
          disabled={refreshing}
          onClick={() => load(false)}
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div>
          <p className="text-xs text-gray-400 mb-1">Views</p>
          <p className="text-xl font-bold text-white">{analytics.totalViews}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Unique visitors</p>
          <p className="text-xl font-bold text-blue-400">{analytics.uniqueVisitors}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Free song req.</p>
          <p className="text-xl font-bold text-slate-200">{analytics.freeSongRequests}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Paid → basket</p>
          <p className="text-xl font-bold text-yellow-400">{analytics.paidSongsAddedToBasket}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Paid checkout</p>
          <p className="text-xl font-bold text-green-400">{analytics.paidSongsCheckout}</p>
        </div>
        <div>
          <p className="text-xs text-gray-400 mb-1">Conversion</p>
          <p className="text-xl font-bold text-cyan-300">{analytics.conversionPct}%</p>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-3 text-center">
        Conversion = paid checkouts ÷ paid added to basket • Tap Refresh to update
      </p>
    </div>
  );
};
